/* eslint-disable unicorn/explicit-length-check */
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserDefaultStatusKey } from "utils/user";
import {
  getFormAccounts,
  getFormErrors,
  getFormPost,
  getFormResults,
  getFormWarnings,
  getOriginalPostState,
  getPlatformFields,
  getPlatformPost,
  getPlatforms as getPlatformsSelector,
  getPostState as getPostStateSelector,
  getSelectedPlatform,
  getVisibleFields,
  PostFormRootState,
} from "selectors/postFormSelectors";

import {
  closePostForm,
  startPostForm,
  updatePostFormField,
} from "actions/postForm";

import { Post, postFormSelector } from "features/post";
import { POST_STATES, PostStatesType } from "constants/PostsView";
import { getUser } from "selectors/commonSelectors";
import { useTrackEvent } from "libs/analytics";
import { CREATE_POST_LINKEDIN_PDF } from "constants/Events";
import { AccountType } from "shared/types/accounts";
import { PlatformFields, PostForm } from "../../../shared/types/post";
import { BulkTabContext, SaveAction, SaveFormResult } from "./types";
import { isNewPostId } from "../../../utils/posts";

const getAccountIds = (
  accountIds: string[] | null | undefined,
  accountId: string | null | undefined
) => {
  if (accountIds && accountIds.length) {
    return accountIds;
  }

  if (accountId) {
    return [accountId];
  }

  return [];
};

export function usePostForm(
  post: Post,
  {
    onSuccess,
    onSave,
  }: {
    onSuccess?: (results: unknown) => void;
    onSave: (args: SaveFormResult, saveAction: SaveAction | undefined) => void;
  },
  bulkTab?: BulkTabContext
): PostForm {
  const dispatch = useDispatch();
  const accounts = useSelector(
    (state: {
      entities: { accounts: { byId: Record<string, AccountType> } };
    }) => state.entities.accounts.byId
  );
  const user = useSelector(getUser);
  const trackEvent = useTrackEvent();

  const { isSubmitting } = useSelector(postFormSelector);
  // account that is selected in planner. If user wants to schedule/queue several posts
  // from planner he can do it only to 1 account
  const { plannerAccount } = post;

  useEffect(() => {
    if (bulkTab) {
      return;
    }
    const { accountIds, ...postData } = post;

    const accountsData = (
      (accountIds || []).length ? accountIds : [postData.accountId]
    )
      .map((id) => accounts[id as string])
      .filter(Boolean);
    dispatch(startPostForm(postData as Post, accountsData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postFormValue = useSelector((state: PostFormRootState) =>
    getFormPost(state, post._id)
  );
  const validationErrors = useSelector((state: PostFormRootState) =>
    getFormErrors(state, post._id)
  );
  const warnings = useSelector((state: PostFormRootState) =>
    getFormWarnings(state, post._id)
  );

  // This indicates what kind of post it will be (draft, queue, schedule)
  const postState = useSelector((state: PostFormRootState) =>
    getPostStateSelector(state, post._id)
  );
  // Objects representing which platforms to use given the accounts
  const platforms = useSelector((state: PostFormRootState) =>
    getPlatformsSelector(state, post._id)
  );

  const originalPostState = useSelector((state: PostFormRootState) =>
    getOriginalPostState(state, post._id)
  );
  // If unlinking a cluster post we will keep track of the fields that deviate.
  const platformFields = useSelector((state: PostFormRootState) =>
    getPlatformFields(state, post._id)
  );

  // Current state of accounts on the form
  const formAccounts = useSelector((state: PostFormRootState) =>
    getFormAccounts(state, post._id)
  );

  // Helper to update a single field for the selected platform.
  const updateField = (field: string, value: unknown) =>
    updateFields({ [field]: value });

  const updateFields = (newValueMap: unknown) => {
    dispatch(updatePostFormField(postFormValue?._id, newValueMap));
  };

  const postHasAvailableStatus = useMemo(
    () => {
      if (user.isPostStatusEnabled) {
        if (user.role !== "collaborator" || !postFormValue?.postStatusKey) {
          return true;
        }

        return (
          user.preferences?.canChangeStatusTo?.includes(
            postFormValue?.postStatusKey
          ) || postFormValue?.postStatusKey === getUserDefaultStatusKey(user)
        );
      }

      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postFormValue?.postStatusKey]
  );

  const postAccountsCanBeUpdated = useMemo(() => {
    const oldValueAccountIds = getAccountIds(post?.accountIds, post?.accountId);
    const newValueAccountIds = getAccountIds(
      postFormValue?.accountIds,
      postFormValue?.accountId
    );

    return (
      !postHasAvailableStatus &&
      JSON.stringify(oldValueAccountIds) !== JSON.stringify(newValueAccountIds)
    );
  }, [
    post?.accountId,
    post?.accountIds,
    postFormValue?.accountId,
    postFormValue?.accountIds,
    postHasAvailableStatus,
  ]);

  const errors = useMemo(() => {
    const errors = { ...validationErrors };

    if (postAccountsCanBeUpdated) {
      if (!errors.type) {
        errors.type = { errors: [] };
      } else if (!errors.type.errors) {
        errors.type = { ...errors.type, errors: [] };
      }

      errors.type.errors.push(
        "You are unable to add an account to a post with an approval status you don't have access to. Change the approval status or remove the newly added account."
      );
    }

    return errors as Record<string, { errors: string[] }> | undefined;
  }, [validationErrors, postAccountsCanBeUpdated]);

  const saveDisabled = useMemo(() => {
    if (postState === POST_STATES.ARCHIVED) return true;

    if (postAccountsCanBeUpdated) {
      return true;
    }

    if (!errors) return false;

    const errorKeys = Object.keys(errors);

    if (errorKeys.length === 1 && errorKeys[0] === "SAVE_ERROR") {
      return false;
    } else if (errorKeys.length > 0) {
      return true;
    }

    return false;
  }, [errors, postAccountsCanBeUpdated, postState]);

  const onSubmit = (saveAction: SaveAction | undefined) => {
    if (isNewPostId(postFormValue?._id) && postFormValue?.type === "document") {
      trackEvent({
        eventName: CREATE_POST_LINKEDIN_PDF,
        metadata: { pages: postFormValue?.meta?.pages },
        useServices: ["AMP", "AC"],
      });
    }

    onSave(
      {
        originalPostState: (post.originalPostType === "draft"
          ? post.originalPostType
          : originalPostState) as PostStatesType,
        newPostState: postState as PostStatesType,
        post: postFormValue as Post,
        platformFields: platformFields as PlatformFields,
        platforms: platforms,
        plannerAccount: plannerAccount as AccountType,
      },
      saveAction
    );
  };

  // Clean up redux when form is unmounted / page navigates
  useEffect(() => {
    if (bulkTab) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => dispatch(closePostForm(post._id)) as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the saga has saved the post, let's call the callback and close the form.
  const formResults = useSelector((state: PostFormRootState) =>
    getFormResults(state, post._id)
  );
  useEffect(() => {
    if (formResults) {
      onSuccess?.(formResults);
      if (!bulkTab) {
        dispatch(closePostForm(post._id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formResults]);

  const platformPost = useSelector((state: PostFormRootState) =>
    getPlatformPost(state, post._id)
  );

  // We show different tabs in case user wants to split off a platform from the cluster post.
  // If only one platform is present then we just show that platform icon, but if there
  // are multiple platforms then we also show a 'link' platform which represents the shared
  // data across all platforms.
  const selectedPlatform = useSelector((state: PostFormRootState) =>
    getSelectedPlatform(state, post._id)
  );

  // What fields are visible for the selected platform?
  const visibleFields = useSelector((state: PostFormRootState) =>
    getVisibleFields(state, post._id)
  );

  const channelId = post?.schedule?.channel || post?.channel || post._id;

  return {
    post,
    postFormValue,
    errors,
    warnings,
    postState,
    platforms,
    originalPostState,
    platformFields,
    formAccounts,
    updateFields,
    updateField,
    saveDisabled,
    onSubmit,
    platformPost,
    selectedPlatform,
    visibleFields,
    channelId,
    isSubmitting,
  };
}
